<template>
  <v-autocomplete v-if="!readOnly && !field.readOnly"
                  ref="inputRef"
                  :search-input="search"
                  no-filter
                  hide-no-data
                  clearable
                  :value="parsedFieldValue"
                  :hint="field.hint"
                  :persistent-hint="true"
                  :required="maybeCall(field.required, value)"
                  :disabled="field.disabled"
                  :chips="field.chips"
                  :items="adressesMap"
                  item-value="value"
                  item-text="text"
                  :rules="validationRules"
                  :label="label"
                  :class="field.class"
                  :prepend-icon="field.prependIcon"
                  :filled="box || field.box"
                  autocomplete="off"
                  return-object
                  @update:search-input="search = $event"
                  @input="onSelectChange"
                  @keypress.stop.enter />
  <div v-else :class="{'reg-information': !small, 'reg-information-small': small}">
    <label>{{ field.label }}</label>
    <p>{{ formattedValue || '-' }}</p>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, getCurrentInstance, toRef } from 'vue'
import { debounce } from 'lodash'
import { useDynamicField } from '../composables/useDynamicField'
import { useFieldFormatting } from '../composables/formatting/useFieldFormatting'
import axios from 'axios'
import CONFIG from '~config/env'
import * as Turf from '@turf/turf'

export default defineComponent({
  name: 'DynamicAddress',
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    field: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    box: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    isValidating: {
      type: Boolean,
      default: false
    },
    itemKey: {
      type: String,
      default: ''
    }
  },
  emits: ['input', 'change'],
  setup(props, { emit, expose }) {
    const instance = getCurrentInstance()
    const $toasted = instance.proxy.$toasted

    const {
      fieldValue,
      validationRules,
      label,
      inputRef,
      maybeCall,
      setFieldValue,
      hasError
    } = useDynamicField({
      value: toRef(props, 'value'),
      field: props.field,
      readOnly: props.readOnly,
      isValidating: toRef(props, 'isValidating'),
      itemKey: props.itemKey,
      emptyValue: undefined,
      emit
    })

    const search = ref(null)
    const parsedFieldValue = ref({})
    const adresses = ref([])
    
    const formattedValue = useFieldFormatting({
      model: computed(() => props.value),
      field: computed(() => props.field)
    })

    const adressesMap = computed(() => {
      return adresses.value
        .map(address => {
          const properties = address.properties || {}
          const context = properties.context || {}
          return {
            text: properties.full_address,
            value: {
              bbox: properties.bbox,
              geom: address.geometry,
              country: context.country?.name,
              state: context.region?.name,
              city: context.place?.name,
              neighborhood: context.neighborhood?.name,
              postalCode: context.postcode?.name,
              street: context.street?.name,
              fullAddress: properties.full_address
            }
          }
        })
        .concat(parsedFieldValue.value ? [parsedFieldValue.value] : [])
    })

    const findAddress = () => {
      const searchValue = (search.value || '').trim()
      if (!searchValue || searchValue.length < 3) {
        return
      }
      const encoded = encodeURIComponent(searchValue)
      const country = props.field.country || 'BR'
      axios.get(`${CONFIG.MAPBOX_GEOCODING_API}?q=${encoded}&country=${country}&access_token=${CONFIG.MAPBOX_PUBLIC_TOKEN}`)
        .then(response => {
          adresses.value = response.data?.features || []
        })
        .catch(error => {
          console.error(error)
        })
    }

    const onFieldValueChange = (value) => {
      if (typeof value === 'object') {
        parsedFieldValue.value = {
          text: value.fullAddress,
          value
        }
        return
      }

      parsedFieldValue.value = value
        ? {
            text: value,
            value: {
              full_address: value
            }
          }
        : undefined
    }

    const onSelectChange = (value) => {
      const address = value?.value || {}
      const fullAddress = address.fullAddress

      if (fieldValue.value === fullAddress) {
        return
      }

      if (props.field.cityRequired) {
        if (!address.city) {
          $toasted.error('O endereço precisa ser, ao menos uma cidade e estado.')
          return
        }
      }

      if (!address.bbox && address.geom) {
        address.bbox = Turf.bbox(address.geom)
      }

      setFieldValue(address)
    }

    // Set up watchers
    watch(fieldValue, onFieldValueChange, { immediate: true })
    watch(search, debounce(findAddress, 300))

    // Expose properties for DynamicForm validation
    expose({
      hasError,
      validate: () => inputRef.value?.validate?.()
    })

    return {
      search,
      parsedFieldValue,
      adressesMap,
      validationRules,
      label,
      inputRef,
      maybeCall,
      onSelectChange,
      formattedValue
    }
  }
})
</script>

<style lang="scss">
</style>